import { toBlob } from 'html-to-image';
import type { Match, Venue } from '@/types/domain';

export type ShareResult = 'shared' | 'downloaded' | 'cancelled';

// Captures the hidden ShareCard element to a 1080x1920 PNG. Caller decides
// what to do with it — preview, share, download.
export async function renderShareCardToFile(
  element: HTMLElement,
  match: Match,
  venue: Venue,
): Promise<File> {
  // Instrument Serif is loaded from Google Fonts; wait for it before capture
  // or the italic-serif venue/VS lockup falls back to Georgia and the design
  // collapses.
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // Continue with whatever's loaded — capture is better than nothing.
    }
  }

  const blob = await toBlob(element, {
    width: 1080,
    height: 1920,
    pixelRatio: 1,
    cacheBust: true,
    backgroundColor: '#0a0a0a',
  });
  if (!blob) throw new Error('Failed to render share card');
  const filename = `nynjwc-${match.id}-${venue.id}.png`;
  return new File([blob], filename, { type: 'image/png' });
}

// Fires the native share sheet (iOS/Android WKWebView, mobile Safari/Chrome).
// Falls back to a file download on desktops without Web Share files support.
// Must be invoked from inside a user-gesture handler.
export async function sendShare(file: File, text: string): Promise<ShareResult> {
  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "I'm going", text });
      return 'shared';
    } catch (err) {
      if ((err as Error).name === 'AbortError') return 'cancelled';
      throw err;
    }
  }
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return 'downloaded';
}

// True when the device can natively share files. Use to label the CTA
// honestly ("Share" vs "Download").
export function canNativeShare(file?: File): boolean {
  if (typeof navigator.canShare !== 'function') return false;
  if (!file) return typeof navigator.share === 'function';
  return navigator.canShare({ files: [file] });
}

// Conventional URL for a pre-generated AI poster for a given match. We try
// home-away first; if that 404s the caller should try the reverse order.
// Returns null for matches with unresolved knockout slots.
export function posterUrlFor(match: Match): string | null {
  if (!match.home || !match.away) return null;
  return `/share/posters/${match.home}-${match.away}.png`;
}

// Probe whether a same-origin image asset exists. Uses Image() onload/onerror
// rather than HEAD because the browser will cache the bytes for the eventual
// <img> render — we don't pay for the request twice.
export function imageExists(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

// Fetch a same-origin asset and wrap it as a File so it can be passed to
// the Web Share API alongside our client-generated PNGs.
export async function fetchAsFile(url: string, filename: string): Promise<File> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || 'image/png' });
}

// Captures the hidden PosterCard element (poster image + venue overlay) to a
// 1080x1920 PNG. Same shape as renderShareCardToFile but accepts an
// additional `posterImageSrc` so we can wait for the underlying image to
// finish loading before capture — without that wait, html-to-image
// occasionally captures the gradient/overlay but no image content.
export async function renderPosterCardToFile(
  element: HTMLElement,
  posterImageSrc: string,
  matchId: string,
  venueId: string,
): Promise<File> {
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // continue with whatever fonts loaded
    }
  }

  // Wait for the actual <img> inside the PosterCard to be fully decoded.
  await new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Poster image failed to load: ${posterImageSrc}`));
    img.src = posterImageSrc;
  });

  const blob = await toBlob(element, {
    width: 1080,
    height: 1920,
    pixelRatio: 1,
    cacheBust: true,
    backgroundColor: '#0a0a0a',
  });
  if (!blob) throw new Error('Failed to render poster card');
  const filename = `nynjwc-${matchId}-${venueId}-poster.png`;
  return new File([blob], filename, { type: 'image/png' });
}
